"use client";

import { Headphones } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/cn";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/ui/reveal";
import { Section, SectionHeading } from "@/components/ui/section";
import { Sheet } from "@/components/ui/sheet";
import { StickyBuyBar } from "@/components/ui/sticky-buy-bar";
import { TabBar } from "@/components/ui/tab-bar";
import { SpecSheet } from "./spec-sheet";

type PurchaseSectionContent = {
  eyebrow: { code: string; name: string; tone: "hirudin" };
  title: string;
  productName: string;
  productNote: string;
  sheetNumber: string;
  specs: readonly { label: string; value: string }[];
  chips: readonly string[];
  price: string;
  points: string;
  buyAction: { label: string; href: string };
  consultAction: { label: string };
  consultSheet: { title: string; body: string; closeLabel: string };
  legal: string;
};

export function HirudinPurchaseSection({
  spec,
}: {
  spec: PurchaseSectionContent;
}) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [buyBarVisible, setBuyBarVisible] = useState(false);
  const [consultOpen, setConsultOpen] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const updateVisibility = () => {
      setBuyBarVisible(sentinel.getBoundingClientRect().top < 0);
    };

    updateVisibility();
    window.addEventListener("scroll", updateVisibility, { passive: true });
    window.addEventListener("resize", updateVisibility);
    return () => {
      window.removeEventListener("scroll", updateVisibility);
      window.removeEventListener("resize", updateVisibility);
    };
  }, []);

  const consultAction = (
    <Button variant="text" onClick={() => setConsultOpen(true)}>
      {spec.consultAction.label}
    </Button>
  );

  const consultSheet = (
    <Sheet
      open={consultOpen}
      onOpenChange={setConsultOpen}
      title={spec.consultSheet.title}
    >
      <p className="text-sm leading-[1.7] text-ink-2">{spec.consultSheet.body}</p>
      <Button
        variant="brand"
        className="mt-5 w-full"
        onClick={() => setConsultOpen(false)}
      >
        {spec.consultSheet.closeLabel}
      </Button>
    </Sheet>
  );

  return (
    <>
      <Section aria-labelledby="hirudin-spec-title">
        <Reveal>
          <SectionHeading
            eyebrow={spec.eyebrow}
            tone="hirudin"
            title={<span id="hirudin-spec-title">{spec.title}</span>}
          />
        </Reveal>
        <Reveal className="mt-6">
          <SpecSheet
            title={spec.productName}
            note={spec.productNote}
            sheetNumber={spec.sheetNumber}
            specs={spec.specs}
            chips={spec.chips}
            price={spec.price}
            points={spec.points}
            buyAction={spec.buyAction}
            consultAction={consultAction}
            legal={spec.legal}
          />
        </Reveal>
        <div
          ref={sentinelRef}
          data-hirudin-sentinel="true"
          aria-hidden="true"
          className="mt-6 h-px w-full"
        />
      </Section>

      {consultSheet}

      <StickyBuyBar
        buyLabel={`${spec.buyAction.label} ¥${spec.price}`}
        buyHref={spec.buyAction.href}
        leftActions={
          <button
            type="button"
            onClick={() => setConsultOpen(true)}
            className="flex size-11 shrink-0 flex-col items-center justify-center gap-px text-[10px] leading-3 text-ink-2 focus-visible:outline-2 focus-visible:outline-forest-500"
          >
            <Headphones className="size-5 stroke-[1.5]" aria-hidden="true" />
            客服
          </button>
        }
        className={cn(
          "motion-safe:transition-transform motion-safe:duration-[240ms] motion-safe:ease-out",
          buyBarVisible ? "translate-y-0" : "pointer-events-none translate-y-full",
        )}
      />
      <TabBar
        className={cn(
          "motion-safe:transition-transform motion-safe:duration-[240ms] motion-safe:ease-out",
          buyBarVisible
            ? "translate-y-[calc(100%+env(safe-area-inset-bottom))] pointer-events-none"
            : "translate-y-0",
        )}
      />
    </>
  );
}
